import { Mic } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isCrmRole } from '../../lib/roles';

/**
 * Where the Scout button lands: the assistant, opened on the mic.
 *
 * `scout=talk` is consumed by AIAgentScreen, which draws a "Tap to talk" bar in
 * place of the empty input. It does NOT start the microphone by itself: the tap
 * on that bar is the user gesture iOS Safari needs to unlock both the mic and
 * the audio element the reply is spoken through. A navigation is not a gesture
 * by the time the next screen mounts, and a mic opened without one leaves every
 * spoken reply silent on an iPhone, which is where reps use this.
 */
export const SCOUT_TALK_PATH = '/agent?scout=talk';

/**
 * The way into Scout for the people who use it: a sales rep has no Overview and
 * no sidebar on a phone, so the assistant has to be on the screens they live
 * on (Meetings, Clients). Mic first — logging a meeting is one spoken sentence.
 *
 * Drawn for CRM_ROLES only. Scout's tools (log_sales_meeting, brief_customer,
 * my_scout_brief) are CRM_TOOL_ROLES in the API, so for anyone else the button
 * would open an assistant that cannot do what the button promises.
 *
 * Primary (amber) on the band: it is the main action on these screens for a
 * rep. A caller with another action on the same band renders that one as the
 * band's secondary shape.
 */
export default function ScoutButton() {
  const navigate = useNavigate();
  const { user } = useAuth();
  if (!isCrmRole(user?.role)) return null;
  return (
    <button
      type="button"
      onClick={() => navigate(SCOUT_TALK_PATH)}
      aria-label="Talk to Scout"
      className="fi-action fi-action--standard fi-action--primary"
      data-testid="scout-button"
    >
      <Mic className="size-4" aria-hidden="true" />
      Scout
    </button>
  );
}
